/**
 * ParcelCard Component
 * 
 * Displays a single parcel with expandable details.
 */

import React, { useState, useCallback } from 'react';
import { View, Text, Pressable, StyleSheet, TextInput, Alert } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { color, space, text, icon, radius } from '../../../shared/tokens/mobile';
import { formatCurrency } from '../../../shared/utils/formatters';
import { DetailRow } from './DetailRow';

export interface Parcel {
  id: number;
  parcel_id: string;
  owner_name?: string;
  address?: string;
  acreage?: number;
  land_value?: number;
  building_value?: number;
  total_value?: number;
  land_use?: string;
  zoning?: string;
  deed_book?: string;
  deed_page?: string;
  sale_date?: string;
  sale_price?: number;
  county_name?: string;
}

interface ParcelCardProps {
  parcel: Parcel;
  index: number;
  isSelected: boolean;
  onPress: () => void;
  onOverride?: (field: 'acreage' | 'total_value', value: number) => void;
}

export function ParcelCard({ parcel, index, isSelected, onPress, onOverride }: ParcelCardProps) {
  const [editingField, setEditingField] = useState<'acreage' | 'total_value' | null>(null);
  const [editValue, setEditValue] = useState('');

  const startEdit = useCallback((field: 'acreage' | 'total_value') => {
    if (!onOverride) return;
    const current = field === 'acreage' ? parcel.acreage : parcel.total_value;
    setEditValue(current != null ? String(current) : '');
    setEditingField(field);
  }, [parcel, onOverride]);

  const saveEdit = useCallback(() => {
    if (!editingField || !onOverride) return;
    const num = parseFloat(editValue.replace(/,/g, ''));
    if (isNaN(num) || num < 0) {
      Alert.alert('Invalid Value', 'Please enter a valid number.');
      return;
    }
    onOverride(editingField, num);
    setEditingField(null);
  }, [editingField, editValue, onOverride]);

  return (
    <Pressable
      style={[styles.card, isSelected && styles.cardSelected]}
      onPress={onPress}
    >
      <View style={styles.header}>
        <View style={styles.badge}>
          <Text style={styles.badgeText}>{index + 1}</Text>
        </View>
        <View style={styles.info}>
          <Text style={styles.parcelId}>{parcel.parcel_id}</Text>
          {parcel.address && <Text style={styles.address}>{parcel.address}</Text>}
        </View>
        <Pressable style={styles.acres} onPress={() => startEdit('acreage')}>
          <Text style={styles.acresValue}>
            {(() => {
              if (parcel.acreage == null) return '—';
              const acres = typeof parcel.acreage === 'number' ? parcel.acreage : parseFloat(String(parcel.acreage)) || 0;
              return isNaN(acres) ? '—' : acres.toFixed(2);
            })()}
          </Text>
          <View style={styles.acresLabelRow}>
            <Text style={styles.acresLabel}>acres</Text>
            {onOverride && <Ionicons name="pencil" size={10} color={color.textMuted} />}
          </View>
        </Pressable>
      </View>

      {/* Inline edit for acreage */}
      {editingField === 'acreage' && (
        <View style={styles.editRow}>
          <Text style={styles.editLabel}>Override Acres:</Text>
          <TextInput
            style={styles.editInput}
            value={editValue}
            onChangeText={setEditValue}
            keyboardType="decimal-pad"
            autoFocus
            selectTextOnFocus
          />
          <Pressable style={styles.editSave} onPress={saveEdit}>
            <Ionicons name="checkmark" size={16} color={color.surface} />
          </Pressable>
          <Pressable style={styles.editCancel} onPress={() => setEditingField(null)}>
            <Ionicons name="close" size={16} color={color.textMuted} />
          </Pressable>
        </View>
      )}

      {/* Inline edit for total value */}
      {editingField === 'total_value' && (
        <View style={styles.editRow}>
          <Text style={styles.editLabel}>Override Value:</Text>
          <TextInput
            style={styles.editInput}
            value={editValue}
            onChangeText={setEditValue}
            keyboardType="decimal-pad"
            autoFocus
            selectTextOnFocus
          />
          <Pressable style={styles.editSave} onPress={saveEdit}>
            <Ionicons name="checkmark" size={16} color={color.surface} />
          </Pressable>
          <Pressable style={styles.editCancel} onPress={() => setEditingField(null)}>
            <Ionicons name="close" size={16} color={color.textMuted} />
          </Pressable>
        </View>
      )}

      {isSelected && (
        <View style={styles.details}>
          <DetailRow label="Owner" value={parcel.owner_name || 'Unknown'} />
          {parcel.county_name && <DetailRow label="County" value={parcel.county_name} />}
          <DetailRow label="Land Use" value={parcel.land_use || '—'} />
          <DetailRow label="Zoning" value={parcel.zoning || '—'} />
          <DetailRow label="Land Value" value={formatCurrency(parcel.land_value)} />
          <DetailRow label="Building Value" value={formatCurrency(parcel.building_value)} />
          <Pressable onPress={() => startEdit('total_value')}>
            <DetailRow label="Total Value" value={formatCurrency(parcel.total_value)} highlight />
          </Pressable>
          {(parcel.sale_date || parcel.sale_price != null) && (
            <DetailRow label="Last Sale" value={`${parcel.sale_date || '—'} - ${formatCurrency(parcel.sale_price)}`} />
          )}
          {(parcel.deed_book || parcel.deed_page) && (
            <DetailRow label="Deed" value={`Book ${parcel.deed_book || '—'}, Page ${parcel.deed_page || '—'}`} />
          )}
        </View>
      )}
    </Pressable>
  );
}

const styles = StyleSheet.create({
  card: {
    backgroundColor: color.surface,
    borderRadius: radius.md,
    padding: space.md,
    marginBottom: space.sm,
    borderWidth: 1,
    borderColor: color.divider,
  },
  cardSelected: {
    borderColor: color.primary,
    borderWidth: 2,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  badge: {
    width: 28,
    height: 28,
    borderRadius: 14,
    backgroundColor: color.primary,
    alignItems: 'center',
    justifyContent: 'center',
  },
  badgeText: {
    color: color.surface,
    fontSize: text.caption.fontSize,
    fontWeight: text.bodyMedium.fontWeight,
  },
  info: {
    flex: 1,
    marginLeft: space.sm,
  },
  parcelId: {
    fontSize: text.body.fontSize,
    fontWeight: text.bodyMedium.fontWeight,
    color: color.textPrimary,
  },
  address: {
    fontSize: text.caption.fontSize,
    color: color.textMuted,
    marginTop: 2,
  },
  acres: {
    alignItems: 'flex-end',
  },
  acresValue: {
    fontSize: text.body.fontSize,
    fontWeight: text.bodyMedium.fontWeight,
    color: color.textPrimary,
  },
  acresLabelRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 3,
  },
  acresLabel: {
    fontSize: text.caption.fontSize,
    color: color.textMuted,
  },
  editRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: space.xs,
    marginTop: space.sm,
    paddingTop: space.sm,
    borderTopWidth: 1,
    borderTopColor: color.divider,
  },
  editLabel: {
    fontSize: text.caption.fontSize,
    color: color.textMuted,
  },
  editInput: {
    flex: 1,
    backgroundColor: color.backgroundSecondary,
    borderRadius: radius.sm,
    paddingHorizontal: space.sm,
    paddingVertical: space.xs,
    fontSize: text.body.fontSize,
    color: color.textPrimary,
    borderWidth: 1,
    borderColor: color.primary,
  },
  editSave: {
    width: 28,
    height: 28,
    borderRadius: 14,
    backgroundColor: color.primary,
    alignItems: 'center',
    justifyContent: 'center',
  },
  editCancel: {
    width: 28,
    height: 28,
    borderRadius: 14,
    backgroundColor: color.backgroundSecondary,
    alignItems: 'center',
    justifyContent: 'center',
  },
  details: {
    marginTop: space.md,
    paddingTop: space.md,
    borderTopWidth: 1,
    borderTopColor: color.divider,
  },
});

